import { useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { formatArea } from "../../../utils/formatting";
import { luckyorangeTrackEven } from "../../../services/lucky-orange/luckyorange-events";
import { TEXT } from "../../../constants/text";
import { TooltipCustom } from "../../ui/tooltip-custom";

// svg
import { ReactComponent as IconBuildingsArea } from "../../../assets/svg/kpi-building-area.svg";
import { ReactComponent as IconTotalBuiltArea } from "../../../assets/svg/totalbuilt-area.svg";
import { ReactComponent as IconFreeArea } from "../../../assets/svg/free-area-2.svg";
import { ReactComponent as IconDownload } from "../../../assets/svg/download.svg";
import { ReactComponent as IconHeart } from "../../../assets/svg/heart.svg";
import { DownloadPopup } from "./download-popup";
import { putResult } from "../../../services/api/results-api";
import { LoaderMUI } from "../../../assets/global/loader/loader-mui";
import { useResultsContext } from "../../../services/context/results-context";
import { useUserContext } from "../../../services/context/user-context";
import { COLORS_GLOBAL } from "../../../constants/colors.constants";

export const ResultItem = ({ result, isSelected, setSelectedResult }) => {
  const { viewOnly } = useUserContext();
  const { setResults } = useResultsContext();
  const kpis = [
    {
      id: "kpi-1",
      name: TEXT.sidebarResults.kpis.totalBuiltUpAreaTitle,
      unit: formatArea(result.kpis.totalBuiltUpArea).unit,
      value: formatArea(result.kpis.totalBuiltUpArea).value,
      icon: "total_build_area",
    },
    {
      id: "kpi-2",
      name: TEXT.sidebarResults.kpis.totalBuildingAreaTitle,
      unit: formatArea(result.kpis.buildingArea).unit,
      value: formatArea(result.kpis.buildingArea).value,
      icon: "building_area",
    },
    {
      id: "kpi-3",
      name: TEXT.sidebarResults.kpis.greenAreaTitle,
      unit: formatArea(result.kpis.freeArea).unit,
      value: formatArea(result.kpis.freeArea).value,
      icon: "free_area",
    },
  ];

  const icons = {
    total_build_area: (
      <IconTotalBuiltArea fill="#343745" width="16px" height="16px" />
    ),
    building_area: (
      <IconBuildingsArea fill="#343745" width="16px" height="16px" />
    ),
    free_area: <IconFreeArea fill="#343745" width="16px" height="16px" />,
  };

  const selectResultHandler = () => {
    luckyorangeTrackEven("Results: select Result");
    setSelectedResult(result);
  };

  const [open, setOpen] = useState(false);

  const openDownloadDialog = (e) => {
    e.stopPropagation();
    setOpen(true);
  };

  const closeDownloadDialog = () => {
    setOpen(false);
  };

  const [favorite, setFavorite] = useState(result.liked);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const favoriteHandler = (e) => {
    e.stopPropagation();
    setLoading(true);
    fetchData(result.resultId, !favorite)
  };

  const fetchData = async (resultId, liked) => {
    if (!resultId) return;
    setLoading(true);
    setError(null);
    try {
      const responseData = await putResult(resultId, { liked: liked });
      setResults(prev => prev.map(r=>r.resultId === resultId ? {...r, liked: liked} : r))
      setFavorite(liked)
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      onClick={selectResultHandler}
      sx={{
        width: "calc(50% - 4px)",
        /* maxWidth: "138px", */
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        padding: "10px",
        boxShadow: 3,
        background: "#fff",
        borderRadius: "8px",
        ":hover": { boxShadow: 6 },
        cursor: "pointer",
        border: "1.5px solid",
        borderColor: isSelected ? "#efa21e" : "#fff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography fontSize={14} fontWeight={600}>
          {result.name ? result.name : `Result`}
        </Typography>
        {/*  <IconButton padding="4px" onClick={openDownloadDialog}>
          <IconDownload stroke="#343745" width="14px" height="14px" />
        </IconButton> */}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          width: "100%",
        }}
      >
        {kpis.map((kpi) => (
          <TooltipCustom key={kpi.id + "tooltip"} text={kpi.name}>
            <Box
              key={kpi.id}
              sx={{
                display: "flex",
                gap: "4px",
                alignItems: "center",
                width: "fit-content",
              }}
            >
              {icons[kpi.icon]}
              <Typography ml={"4px"} fontSize={12} fontWeight={400}>
                {kpi.value}
              </Typography>
              <Typography fontSize={10} fontWeight={400}>
                {kpi.unit}
              </Typography>
            </Box>
          </TooltipCustom>
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: "8px",
          alignItems: "center",
          justifyContent: "space-around",
          position: "relative",
        }}
      >
        {loading ? (
          <Box
            sx={{
              width: "30px",
              position: "relative",
            }}
          >
            <LoaderMUI size={10} />
          </Box>
        ) : (
          <IconButton padding="4px" onClick={favoriteHandler} disabled={viewOnly}>
            <IconHeart
              stroke={viewOnly ? COLORS_GLOBAL.BTN_DISABLED : COLORS_GLOBAL.MAIN_DARK}
              fill={favorite ?  "#343745" : "none"}
              width="14px"
              height="14px"
            />
          </IconButton>
        )}
        <Box sx={{ width: "2px", height: "70%", backgroundColor: "#EBEBEC" }} />
        <IconButton padding="4px" onClick={openDownloadDialog} /* disabled={viewOnly} */>
          <IconDownload stroke={/* viewOnly ? COLORS_GLOBAL.BTN_DISABLED : */ COLORS_GLOBAL.MAIN_DARK} width="14px" height="14px" />
        </IconButton>
      </Box>
      <DownloadPopup
        open={open}
        onClose={closeDownloadDialog}
        resultName={result.name ? result.name : `Result`}
        fileDXF={result.fileDXF}
        fileXLSX={result.fileXLSX}
      />
    </Box>
  );
};
